// Theme preference persistence, shared across *.bitquery.io apps.
//
// The choice lives in the `bq-theme` cookie scoped to .bitquery.io, so a theme
// picked in any subdomain (ide / account / www / moneyflow / …) is read by all
// the others. A legacy per-origin localStorage mirror is kept for back-compat.
// Mirrors graphql-ide's src/store/themeStore.js so the cookie format matches.

const STORAGE_KEY = 'bq-ide-theme' // localStorage (per-origin) — kept for back-compat
const COOKIE_KEY = 'bq-theme' // cookie shared across *.bitquery.io subdomains

// User-selectable preferences. 'system' follows the OS colour scheme.
export const THEME_PREFERENCES = ['system', 'light', 'dark'] as const
export type ThemePreference = (typeof THEME_PREFERENCES)[number]

const isPreference = (val: unknown): val is ThemePreference =>
    typeof val === 'string' && (THEME_PREFERENCES as readonly string[]).includes(val)

// On a real bitquery.io host, scope the cookie to the whole domain so every
// subdomain reads the same choice. On localhost or a preview host, omit the
// domain so it's a normal host-only cookie.
function cookieDomainAttr(): string {
    try {
        const host = window.location.hostname || ''
        if (host === 'bitquery.io' || host.endsWith('.bitquery.io')) return '; domain=.bitquery.io'
    } catch {
        /* noop */
    }
    return ''
}

function readCookiePreference(): ThemePreference | null {
    try {
        const match = document.cookie.match(/(?:^|;\s*)bq-theme=([^;]+)/)
        const val = match && decodeURIComponent(match[1])
        if (isPreference(val)) return val
    } catch {
        /* cookies unavailable */
    }
    return null
}

function writeCookiePreference(pref: ThemePreference): void {
    try {
        const secure = window.location.protocol === 'https:' ? '; Secure' : ''
        const value = encodeURIComponent(pref)
        // 1 year; readable by JS (no HttpOnly) so each app can apply it client-side.
        document.cookie = `${COOKIE_KEY}=${value}; path=/; max-age=31536000${cookieDomainAttr()}; SameSite=Lax${secure}`
    } catch {
        /* noop */
    }
}

// Domain-wide cookie wins (a choice made on any *.bitquery.io app), then the
// legacy per-origin localStorage value, then the OS default.
export function getThemePreference(): ThemePreference {
    const fromCookie = readCookiePreference()
    if (fromCookie) return fromCookie
    try {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (isPreference(saved)) return saved
    } catch {
        /* localStorage unavailable — fall back to system */
    }
    return 'system'
}

export function setThemePreference(pref: ThemePreference): void {
    const next = isPreference(pref) ? pref : 'system'
    writeCookiePreference(next)
    try {
        localStorage.setItem(STORAGE_KEY, next)
    } catch {
        /* ignore persistence failures */
    }
}

export function systemPrefersDark(): boolean {
    try {
        return (
            typeof window !== 'undefined' &&
            !!window.matchMedia &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
        )
    } catch {
        return false
    }
}

// Subscribe to OS colour-scheme changes (relevant while preference is 'system').
// Returns an unsubscribe function.
export function subscribeSystemDark(onChange: (dark: boolean) => void): () => void {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {}
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => onChange(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
}

// ---------------------------------------------------------------------------
// Module-level store, shared by every useDarkMode() consumer so a change in one
// component (e.g. the menu) re-renders all of them (e.g. the layout's theme).
// ---------------------------------------------------------------------------

export type ThemeState = { preference: ThemePreference; systemDark: boolean }

let state: ThemeState = { preference: getThemePreference(), systemDark: systemPrefersDark() }
const listeners = new Set<() => void>()

// Keep the page background (index.html / App.scss) in step with the resolved theme
function applyDocumentTheme({ preference, systemDark }: ThemeState): void {
    try {
        const dark = preference === 'system' ? systemDark : preference === 'dark'
        document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light')
    } catch {
        /* no document */
    }
}

applyDocumentTheme(state)

function setState(next: Partial<ThemeState>): void {
    state = { ...state, ...next }
    applyDocumentTheme(state)
    listeners.forEach((l) => l())
}

// Follow the OS scheme module-wide (relevant while the preference is 'system').
subscribeSystemDark((systemDark) => setState({ systemDark }))

export function subscribeTheme(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

// Stable reference between changes — required by useSyncExternalStore.
export function getThemeState(): ThemeState {
    return state
}

export function setPreference(pref: ThemePreference): void {
    const next = isPreference(pref) ? pref : 'system'
    setThemePreference(next) // persist to the shared cookie (+ legacy localStorage)
    setState({ preference: next })
}
